import useAttendance from 'v3/pages/ControlTower/Kanban/Attendance/useAttendance';
import AttendanceNotice from 'v3/pages/ControlTower/Kanban/Attendance/Notice';
import theme from 'v3/theme';
import { attendancePrefill } from 'v3/pages/ControlTower/Kanban/Attendance/prefill';
import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { cooperplaceApi } from 'services/api';
import useModal from 'hooks/useModal';
import { useSnackbar } from 'v3/components/Snackbar';
import Text from 'v3/components/Text';
import Button from 'v3/components/Button';
import ApplicationLayout from 'v3/layouts/Application';
import { currencyOptions, makeConversion } from 'v3/utils/formatter';
import { BlockedTravelContext } from 'contexts/BlockedTravelProvider';
import { validatePolicyValidity } from 'v3/validators/loadValidators';
import { loadToFreightData, loadReplacementToFreightData } from 'v3/components/FreightValues/utils';
import {
  InsuranceValueContextProvider,
  useInsuranceValue,
} from 'contexts/InsuranceValueContext';
import CreatedTravelModal from './Modals/CreatedTravel';
import HasTravelsModal from './Modals/HasTravel';
import { MinimumMarginModal } from '../shared';
import Load from './Load';
import Shipper from './Shipper';
import Driver from './Driver';
import Locale from './Locale';
import Vehicle from './Vehicle';
import Attachments from './Attachments';
import { TravelContext, handleCreation } from './controller';
import { GROCard } from './GRO';
import ConflictDriverModal from '../shared/Modal/ConflictDriver';
import moment from 'moment';
import { DELIVERY_TYPE_OPTIONS } from 'v3/utils/constants';
import FreightValues from './FreightValues';
import { fetchCountries } from 'utils/fetches';
import { has } from 'lodash';

const [AddTravelProvider] = TravelContext;

export function TravelRegisterContent() {
  const {
    isFetchingBenner,
    updateItensToFetchMargin,
    margins,
    isFetchingMargin,
    setItemsToSetDefaultValues,
  } = useContext(BlockedTravelContext);
  const hasTravelsModal = useModal();
  const createdTravelModal = useModal();
  const minimumFreightValueModal = useModal();
  const conflictDriverModal = useModal();
  const freightValuesRef = useRef();
  const freightValuesReplaceRef = useRef();
  const { loading: isFetchingInsuranceValue } = useInsuranceValue();

  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const params = useParams();
  const attendance = useAttendance(params.loadId, 'LOAD', { enforceAccess: true });
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [candidateError, setCandidateError] = useState(null);

  const [data, setData] = useState({
    origin: null,
    destinations: [],
  });
  const [isGROLoad, setIsGROLoad] = useState(null);
  const [fetchingLoadGRORange, setFetchingLoadGRORange] = useState(false);
  const [loadGRORangeData, setLoadGRORangeData] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [modalName, setModalName] = useState('');
  const [countryList, setCountryList] = useState([]);
  const [tollsPlazaInfos, setTollsPlazaInfos] = useState();

  const [showTollCard, setShowTollCard] = useState(false);

  function showSnackbar(text, options) {
    snackbar.show(<Text> {text} </Text>, options);
  }

  const getTollsPlazaByLoadId = async (loadId) => {
    try {
      const response = await cooperplaceApi.get(`tollsPlaza/by-load-id/${loadId}`);
      setTollsPlazaInfos(response?.data);
    } catch (error) {
      snackbar.show(<Text>{error?.response?.data?.message || 'Erro ao listar as praças de pedágio'}</Text>, {
        type: 'error',
      });
    }
  };

  async function fetchLoadGRORange(policyId, rangeId) {
    try {
      setFetchingLoadGRORange(true);
      const response = await cooperplaceApi.get(`policies/${policyId}/ranges/${rangeId}`);
      setLoadGRORangeData(response.data);
      setFetchingLoadGRORange(false);
      return response.data;
    } catch (error) {
      showSnackbar(
        'Erro ao recuperar detalhes da apólice associada à carga. Tente novamente mais tarde',
        { type: 'error' },
      );
      setFetchingLoadGRORange(false);
      return navigate('/viagens');
    }
  }

  async function setFreightValues(loadData) {
    const props = loadToFreightData(loadData);
    await freightValuesRef.current?.init({
      ...props,
      attended: true,
    });
    if (loadData?.isReplacement) {
      const replacementProps = loadReplacementToFreightData(loadData, loadData?.travel);
      await freightValuesReplaceRef.current?.init({
        ...replacementProps,
        attended: true,
      });
    }
  }

  useEffect(() => {
    async function fetchLoad() {
      if (params.loadId) {
        try {
          const cooperLoad = await cooperplaceApi.get(`loads/${params.loadId}`);

          const loadData = cooperLoad.data;
          await setFreightValues(loadData);

          if (loadData.loadPolicy?.id && loadData?.loadPolicy?.policy?.dueDate) {
            const isValid = validatePolicyValidity(loadData?.loadPolicy?.policy?.dueDate);
            if (!isValid) {
              snackbar.show(
                <Text>
                  Não é possível atender carga atrelada à apólice vencida! Entre em contato com o
                  setor de GRO
                </Text>,
                {
                  type: 'error',
                },
              );
              setIsLoading(false);

              return navigate('/viagens');
            }
          }

          if (loadData && loadData.deletedAt) {
            showSnackbar('Não é possível atender uma carga cancelada!', {
              type: 'error',
            });
          } else {
            const issueRateItem = loadData?.shipmentDiscounts?.find(
              (item) => item.label.toLowerCase() === 'taxa de emissão',
            );

            const dollarValue = loadData.freightExchange;
            const isBRLCurrency = loadData.currency === 'BRL';
            const valuesCurrency = loadData.currency;
            const [origin] = loadData.loadOrigins;

            const countries = await fetchCountries();
            setCountryList(countries);

            const toLocation = (location) => ({
              ...location,
              scheduled_time: moment(location.scheduledTime).format('YYYY-MM-DD HH:mm'),
              start_schedule: moment(location.startSchedule).format('YYYY-MM-DD HH:mm'),
              identifier: location.id,
              city: location.cityModel
                ? location.cityModel
                : {
                    name: location.city,
                  },
              province: location.cityModel ? location.cityModel.province?.name : location.province,
              formatted_address: location.formattedAddress,
              country: countries.find((country) => country.abbreviation === location.countrySlug),
              zip_code: location.zipCode,
            })

            const polylineRouteFromLoad =
              loadData?.polylineRoute ||
              loadData?.polyline_route ||
              loadData?.preTravel?.polylineRoute ||
              loadData?.preTravel?.polyline_route ||
              loadData?.preTravel?.polyline ||
              loadData?.routes?.polyline ||
              null;

            const polylineWaypointsFromLoad =
              loadData?.polylineWaypoints ||
              loadData?.polyline_waypoints ||
              loadData?.preTravel?.polylineWaypoints ||
              loadData?.preTravel?.polyline_waypoints ||
              loadData?.routes?.polylineWaypoints ||
              loadData?.routes?.polyline_waypoints ||
              null;

            setData(old => ({
              ...old,
              isReplacement: loadData.isReplacement,
              crawledLoad: loadData?.crawledLoad,
              polylineRoute: polylineRouteFromLoad,
              polylineWaypoints: polylineWaypointsFromLoad,
              vehicleTypes: loadData?.vehicleTypes || [],
              vehicleImplementTypes: loadData?.vehicleImplementTypes || [],
              takerValue: loadData?.takerValue,
              origin: {
                ...toLocation(origin),
                type: DELIVERY_TYPE_OPTIONS.find(type => type.id === 'collect'),

              },
              destinations: loadData.loadDestinations
                .filter(destination => destination.action !== 'via')
                .map((destination) => ({
                  ...toLocation(destination),
                  type: DELIVERY_TYPE_OPTIONS.find((type) => type.id === destination.action),
                })),
              exchange: loadData.exchange,
              currency: currencyOptions.find(
                (currency) => currency.value === loadData?.loadCurrency,
              ),
              loadId: loadData.id,
              loadProductId: loadData?.productId,
              client: loadData.taker,
              tags: loadData.tags,
              riskManager: loadData.riskManagers ? loadData.riskManagers[0] : null,
              tracker: loadData.riskManager,
              userCreated: loadData.userCreated,
              costCenter: loadData.costCenter,
              cargo_value: loadData.cargoValue,
              advance_money_percentage_external: loadData.advanceMoneyPercentageExternal || 0,
              advance_money_percentage: loadData?.advanceMoneyPercentage || 0,
              toll_value: loadData.tollValue,
              tollObservation: loadData?.tollObservation,
              discharge_value: loadData.dischargeValue,
              dischargeObservation: loadData?.dischargeObservation,
              ferry: loadData.ferry,
              ferryObservation: loadData?.ferryObservation,
              fare_company: loadData?.fareCompany,
              editableFareCompany: loadData?.fareCompany,
              charge_value: loadData?.chargeValue,
              chargeObservation: loadData?.chargeObservation,
              gris: loadData?.gris,
              total_inputs: loadData?.totalInputs,
              editableTotalInputs: loadData?.totalInputs,
              total_taker_value: loadData?.totalTakerValue,
              ad_valorem: loadData?.adValorem,
              grissAdObservation: loadData?.grissAdObservation,
              pcp_observation: loadData.pcpObservation,
              isBRLCurrency,
              load_value_type: loadData?.loadValueType,
              negotiation_type: loadData?.negotiationType,
              freight_values_currency: valuesCurrency,
              freight_values_exchange: loadData.freightExchange,
              exchange_day: loadData?.exchangeDay,
              total_shipment_transfer: loadData?.totalShipmentTransfer,
              total_shipment_discounts: loadData?.totalShipmentDiscounts,
              issueRateId: issueRateItem?.id,
              issueRate: issueRateItem?.value,
              container: loadData?.container,
              shipmentDiscounts: loadData?.shipmentDiscounts
                ?.filter((item) => item.label.toLowerCase() !== 'taxa de emissão')
                .map((item) => ({
                  ...item,
                  value:
                    valuesCurrency === 'BRL'
                      ? item.value
                      : +makeConversion(item.value, '*', dollarValue).toFixed(
                        2
                      ),
                })),
              shipmentTransfer: loadData.shipmentTransfers.map((item) => ({
                ...item,
                value:
                  valuesCurrency === 'BRL'
                    ? item.value
                    : +makeConversion(item.value, '*', dollarValue).toFixed(2),
              })),
              company: loadData?.company,
              loadGROData: loadData?.loadPolicy?.id
                ? {
                  policyId: loadData?.loadPolicy?.policyId,
                  techTracker: null,
                  modelTracker: null,
                  trackerNumber: null,
                  immobilizer: null,
                  immobilizerNumber: null,
                  locator: null,
                  locatorNumber: null,
                  bait1: null,
                  baitNumber1: null,
                  bait2: null,
                  baitNumber2: null,
                  bait3: null,
                  baitNumber3: null,
                  bait4: null,
                  baitNumber4: null,
                  groupEquipment: null,
                  riskManagerSearch: null,
                }
                : null,
            }));

            setIsGROLoad(loadData?.loadPolicy?.id ? loadData?.loadPolicy : null);
          }
          if (loadData?.loadPolicy?.id) {
            const rgDetails = await fetchLoadGRORange(
              loadData?.loadPolicy?.policyId,
              loadData?.loadPolicy?.policyRangeId,
            );
            setData((old) => ({
              ...old,
              loadGROData: {
                ...old?.loadGROData,
                checklistType: rgDetails?.checklist_tracker,
              },
            }));
          }

          setIsLoading(false);
        } catch (ex) {
          // Handle exception
          setIsLoading(false);
        }
      }
    }
    fetchLoad();
  }, [params.loadId]);

  useEffect(() => {
    if (params.loadId) {
      getTollsPlazaByLoadId(params.loadId);
    }
  }, [params.loadId]);

  useEffect(() => {
    if (!attendance.record) return undefined;
    let active = true;
    setCandidateLoading(true);
    setCandidateError(null);
    attendancePrefill(attendance.record)
      .then((prefill) => {
        if (active) setData((old) => ({ ...old, ...prefill }));
      })
      .catch((error) => {
        if (active) setCandidateError(error.message || 'Não foi possível carregar o candidato.');
      })
      .finally(() => {
        if (active) setCandidateLoading(false);
      });
    return () => {
      active = false;
    };
  }, [attendance.record]);

  const value = {
    data,
    setData,
    showTollCard,
    setShowTollCard,
    setIsLoading,
    errors,
    setErrors,
    modalName,
    setModalName,
    isGROLoad,
    setIsGROLoad,
    countryList,
    loadGRORangeData,
    fetchingLoadGRORange,
    tollsPlazaInfos,
    setTollsPlazaInfos
  };

  const saveTravel = async ({
    confirmMinimiumMargin = data?.confirmMinimiumMargin,
    passDriverValidation = data?.passDriverValidation,
  } = {}) => {

    try {
      if (attendance.error || attendance.accessPending || candidateError || candidateLoading) return;
      const freightValues = await freightValuesRef?.current?.getValues({ validateAttended: true });

      const replacementFreightValues = data?.isReplacement
        ? await freightValuesReplaceRef?.current?.getValues({ validateAttended: true })
        : null;

      const setModalNameSafe = (name) => {
        if (!name) return;
        setModalName(name);
      };

      const result = await handleCreation({
        data: { ...data, isGROLoad, controlTower: attendance.record },
        tollsPlazaInfos,
        freightValues,
        replacementFreightValues,
        setData,
        setErrors,
        showSnackbar,
        setIsLoading,
        setModalName: setModalNameSafe,
        margins,
        confirmMinimiumMargin,
        loadGRORangeData,
        passDriverValidation,
      });
      if (result && attendance.record) attendance.back();

    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (modalName === 'DriverHasTravels') {
      hasTravelsModal.open();
      return;
    }
    if (modalName === 'CreatedTravel') {
      createdTravelModal.open();
    }
    if (modalName === 'MinimumMargin') {
      minimumFreightValueModal.open();
    }
    if (modalName === 'ConflictDriver') {
      conflictDriverModal.open();
    }
  }, [modalName]);

  useEffect(() => {
    updateItensToFetchMargin({
      loadId: data?.loadId,
      origin: data?.origin,
      destinations: data?.destinations,
      shippingCompany: data?.shippingCompany?.shippingCompanies,
      mainVehicle: data?.vehicle?.main,
    });
  }, [data?.loadId, data?.origin, data?.destinations, data?.shippingCompany, data?.vehicle?.main]);

  useEffect(() => {
    if (data?.costCenter?.businessId)
      setItemsToSetDefaultValues((old) => ({
        ...old,
        costCenterBusinessId: data?.costCenter?.businessId,
      }));
  }, [data?.costCenter]);

  useEffect(() => {
    if (params.dealId) {
      setData((old) => ({
        ...old,
        dealId: Number(params.dealId),
      }));
    }
  }, [params.dealId]);

  const lastDestination = useMemo(() => {
    if (!data?.destinations?.length) return null;
    return data?.destinations?.[data?.destinations?.length - 1];
  }, [data?.destinations]);

  const isNational = useMemo(() => {
    return data?.origin?.countrySlug === 'br' && lastDestination?.countrySlug === 'br';
  }, [data?.origin, lastDestination]);

  return (
    <ApplicationLayout
      title="Cadastro de viagem"
      RightComponent={
        <>
          <Button
            id="button-cancelar-viagem"
            variant="secondary"
            disabled={Boolean(attendance.record) && isLoading}
            onClick={() => attendance.record || attendance.error ? attendance.back() : navigate('/viagens')}
          >
            <Text weight={500} color="dark" type="regular">
              Cancelar
            </Text>
          </Button>
          <Button
            id="button-salvar-viagem"
            variant="success"
            onClick={() => saveTravel()}
            loading={isLoading || isFetchingBenner || isFetchingMargin || isFetchingInsuranceValue}
            disabled={data.invalidLoad || (Boolean(attendance.error) || attendance.accessPending) || Boolean(candidateError) || candidateLoading}
          >
            <Text weight={500} color="white" type="regular">
              Salvar viagem
            </Text>
          </Button>

        </>
      }
    >
      <AttendanceNotice attendance={attendance} busy={isLoading}>
        {candidateLoading && <p role="status">Carregando o candidato selecionado…</p>}
        {candidateError && <p role="alert" style={{ color: theme.colors.danger }}>{candidateError}</p>}
      </AttendanceNotice>
      <AddTravelProvider value={value}>
        <HasTravelsModal isOpen={hasTravelsModal.isOpen} onClose={hasTravelsModal.close} />
        <CreatedTravelModal
          isOpen={createdTravelModal.isOpen}
          onClose={createdTravelModal.close}
          notClosed={false}
        />
        <MinimumMarginModal
          isOpen={minimumFreightValueModal.isOpen}
          onClose={() => {
            setData((old) => ({ ...old, confirmMinimiumMargin: false }));
            minimumFreightValueModal.close();
            setModalName('');
            setErrors({});
          }}
          confirm={({ confirmMinimiumMargin }) => {
            setData((old) => ({ ...old, confirmMinimiumMargin }));
            minimumFreightValueModal.close();
            saveTravel({ confirmMinimiumMargin });
          }}
          isNewTravel
        />
        {errors?.travelsWithDriver?.length > 0 && (
          <ConflictDriverModal
            isOpen={conflictDriverModal.isOpen}
            onClose={() => {
              setData((old) => ({ ...old, passDriverValidation: false }));
              conflictDriverModal.close();
              setModalName('');
              setErrors({});
            }}
            onConfirm={({ passDriverValidation }) => {
              setData((old) => ({ ...old, passDriverValidation }));
              conflictDriverModal.close();
              saveTravel({ passDriverValidation });
            }}
            travels={errors.travelsWithDriver}
            travelData={data}
          />
        )}

        <Row>
          <Col md={6} xs={12}>
            <Load />
          </Col>
          <Col md={6} xs={12}>
            <Shipper />
            <Driver />
            <Vehicle
              freightValuesRef={freightValuesRef}
              freightValuesReplaceRef={freightValuesReplaceRef}
            />
            <GROCard />
            <Attachments />
          </Col>
          <Col md={12} xs={12}>
            <Locale
              freightValuesRef={freightValuesRef}
              freightValuesReplaceRef={freightValuesReplaceRef}
            />
          </Col>
          <Col md={12} xs={12}>
            <FreightValues
              freightValuesRef={freightValuesRef}
              freightValuesReplaceRef={freightValuesReplaceRef}
              isReplacement={data?.isReplacement}
              shippingCompany={data?.shippingCompany}
              cargoValue={data?.cargo_value}
              isLoading={isLoading}
              isNational={isNational}
            />
          </Col>
        </Row>
      </AddTravelProvider>
    </ApplicationLayout>
  );
}

export default function TravelRegister() {
  return (
    <InsuranceValueContextProvider>
      <TravelRegisterContent />
    </InsuranceValueContextProvider>
  );
}
